import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Box,
  makeStyles,
  createStyles,
  Theme,
  CircularProgress,
} from '@material-ui/core';
import { SummaryCards } from '../SummaryCard';
import { ToggleButton } from '../ToggleButton';
import { SearchField } from '../SearchFilters/SearchField';
import { DropdownField } from '../SearchFilters/DropdownField';
import { PromptList } from '../PromptList';
import { UserPromptList } from '../PromptList/UserPromptList';
import { PromptPageProps } from './types';
import BackButton from '../../../utils/backButton';
import { HelpComponent } from '../../../utils/helpComponent';
import HELP_URL from '../../../utils/helpLinkConstant';
import { useApi } from '@backstage/core-plugin-api';
import { axiosInstanceProviderApiRef } from '../../../../apis/axiosInstanceApi';
import { PromptAnalyticsApi, IntervalKeyword } from '../../../../apis/promptAnalyticsApi';

import { transformAnalyticsData } from './dataTransformer';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    summarySection: {
      marginBottom: theme.spacing(6),
    },
    controlsSection: {
      marginBottom: 0,
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    searchContainer: {
      marginLeft: 'auto',
    },
    title: {
      fontWeight: 500,
    },
    listSection: {
      marginTop: theme.spacing(3),
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
    },
    errorContainer: {
      padding: theme.spacing(3),
      textAlign: 'center',
      color: theme.palette.error.main,
    },
  }),
);

export const PromptPage: React.FC<PromptPageProps> = ({
  onSummaryCardClick,
  onToggleChange,
  onSearchChange,
  onFilterChange,
}) => {
  const classes = useStyles();
  const { domainId } = useParams<{ domainId?: string }>();
  const axiosInstanceApi = useApi(axiosInstanceProviderApiRef);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [promptData, setPromptData] = useState<any[]>([]);
  const [userPromptData, setUserPromptData] = useState<any>(null);
  const [interval, setInterval] = useState<IntervalKeyword>('3m');
  
  const [toggleValue, setToggleValue] = useState('similar');
  const [searchValue, setSearchValue] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState('');
  const [selectedPersona, setSelectedPersona] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!domainId) {
        setError('Domain ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const axiosInstance = await axiosInstanceApi.getInstance();
        const api = new PromptAnalyticsApi(axiosInstance);
        const response = await api.getLatestReport(domainId, interval);
        
        if (!response) {
          throw new Error('No data received from server');
        }
        
        const transformed = transformAnalyticsData(response);
        setSummaryData(transformed.summaryData);
        setPromptData(transformed.promptData);
        setUserPromptData(transformed.userPromptData);
      } catch (err: any) {
        console.error('Error fetching prompt analytics:', err);
        setError(err.message || 'Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [domainId, interval, axiosInstanceApi]);

  // Fetch detailed user history when switching to "by user" view
  useEffect(() => {
    const fetchUserHistory = async () => {
      if (toggleValue !== 'user' || !userPromptData || !domainId || !userPromptData.users) {
        return;
      }

      // Check if users already have detailed prompts to avoid refetching
      const hasDetailedPrompts = userPromptData.users.some((user: any) => 
        user.prompts && user.prompts.length > 0 && typeof user.prompts[0] === 'string'
      );
      
      if (hasDetailedPrompts) {
        return; // Already fetched
      }

      try {
        const axiosInstance = await axiosInstanceApi.getInstance();
        const api = new PromptAnalyticsApi(axiosInstance);
        
        // Fetch detailed history for each user
        const usersWithHistory = await Promise.all(
          userPromptData.users.map(async (user: any) => {
            try {
              const history = await api.getRecentHistory(user.email, domainId, 100);
              return {
                ...user,
                prompts: history.prompts.length > 0 
                  ? history.prompts.slice(0, 7).map((p: any) => p.prompt_text || p)
                  : user.prompts, // Fallback to original prompts if no history
              };
            } catch (err) {
              console.error(`Error fetching history for ${user.email}:`, err);
              return user; // Keep original user data if fetch fails
            }
          })
        );

        setUserPromptData({
          ...userPromptData,
          users: usersWithHistory,
        });
      } catch (err: any) {
        console.error('Error fetching user history:', err);
        // Don't set error state, just use existing data
      }
    };

    fetchUserHistory();
  }, [toggleValue, userPromptData, domainId, axiosInstanceApi]);

  const handleToggleChange = (value: string) => {
    setToggleValue(value);
    onToggleChange?.(value);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const handleMonthChange = (date: Date | null) => {
    setSelectedMonth(date);
    onFilterChange?.({ month: date, subdomain: selectedSubdomain, persona: selectedPersona });
  };

  const handleIntervalChange = (value: string) => {
    setInterval(value as IntervalKeyword);
  };

  const handleSubdomainChange = (value: string) => {
    setSelectedSubdomain(value);
    onFilterChange?.({ month: selectedMonth, subdomain: value, persona: selectedPersona });
  };

  const handlePersonaChange = (value: string) => {
    setSelectedPersona(value);
    onFilterChange?.({ month: selectedMonth, subdomain: selectedSubdomain, persona: value });
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">Error loading analytics</Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <BackButton />
        <HelpComponent helpUrl={HELP_URL.PROMPT_ANALYTICS} />
      </Grid>

      {/* Top Section - Summary Cards */}
      <Grid item xs={12} className={classes.summarySection}>
        <SummaryCards
          cards={summaryData ?? []}
          columns={4}
          onCardClick={onSummaryCardClick}
        />
      </Grid>

      {/* Middle Section - Filters with Toggle */}
      <Grid item xs={12} className={classes.controlsSection}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={2} className={classes.toggleContainer}>
            <ToggleButton
              value={toggleValue}
              onChange={handleToggleChange}
            />
          </Grid>
          <Grid item xs={12} md={3} className={classes.searchContainer}>
            <SearchField
              value={searchValue}
              onChange={handleSearchChange}
              onSearch={handleSearchChange}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DropdownField
              label="Time Range"
              value={interval}
              onChange={handleIntervalChange}
              options={[
                { value: '1w', label: 'Last Week' },
                { value: '2w', label: 'Last 2 Weeks' },
                { value: '3w', label: 'Last 3 Weeks' },
                { value: '1m', label: 'Last Month' },
                { value: '2m', label: 'Last 2 Months' },
                { value: '3m', label: 'Last 3 Months' },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DropdownField
              label="Filter by sub-domain"
              value={selectedSubdomain}
              onChange={handleSubdomainChange}
              options={[
                { value: 'ui', label: 'UI Components' },
                { value: 'data', label: 'Data Analysis' },
                { value: 'marketing', label: 'Marketing' },
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <DropdownField
              label="Filter by persona"
              value={selectedPersona}
              onChange={handlePersonaChange}
              options={[
                { value: 'developer', label: 'Developer' },
                { value: 'designer', label: 'Designer' },
                { value: 'analyst', label: 'Analyst' },
              ]}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Bottom Section - Prompt List */}
      <Grid item xs={12} className={classes.listSection}>
        {toggleValue === 'similar' ? (
          <PromptList prompts={promptData ?? []} />
        ) : (
          userPromptData && <UserPromptList {...userPromptData} />
        )}
      </Grid>
    </Grid>
  );
};
